/**
 * Notification Service - Centralized notification management
 * Provides consistent toast notifications and feedback throughout the app
 */

import toast, { type ToastOptions } from 'react-hot-toast';

interface NotificationOptions extends ToastOptions {
  action?: {
    label: string;
    onClick: () => void;
  };
}

class NotificationService {
  /**
   * Show success notification
   */
  success(message: string, options?: NotificationOptions): string {
    return toast.success(message, {
      duration: 3000,
      ...options,
    });
  }

  /**
   * Show error notification
   */
  error(message: string, options?: NotificationOptions): string {
    return toast.error(message, {
      duration: 5000,
      ...options,
    });
  }

  /**
   * Show info notification
   */
  info(message: string, options?: NotificationOptions): string {
    return toast(message, {
      duration: 4000,
      icon: 'ℹ️',
      ...options,
    });
  }

  /**
   * Show warning notification
   */
  warning(message: string, options?: NotificationOptions): string {
    return toast(message, {
      duration: 4000,
      icon: '⚠️',
      style: {
        background: '#FEF3C7',
        color: '#92400E',
        border: '1px solid #F59E0B',
      },
      ...options,
    });
  }

  /**
   * Show loading notification
   */
  loading(message: string, options?: ToastOptions): string {
    return toast.loading(message, {
      ...options,
    });
  }

  /**
   * Show custom notification
   */
  custom(message: string, options?: NotificationOptions): string {
    return toast(message, {
      duration: 4000,
      ...options,
    });
  }

  /**
   * Show notification with action button
   */
  withAction(
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info'
  ): string {
    const toastId = this[type](message, {
      duration: 6000,
    });

    // Note: react-hot-toast doesn't support action buttons out of the box
    // This is a placeholder for future enhancement or custom toast implementation
    return toastId;
  }

  /**
   * Dismiss specific notification
   */
  dismiss(toastId?: string): void {
    toast.dismiss(toastId);
  }

  /**
   * Dismiss all notifications
   */
  dismissAll(): void {
    toast.dismiss();
  }

  /**
   * Update existing notification
   */
  update(toastId: string, message: string, type: 'success' | 'error' | 'loading' = 'success'): void {
    if (type === 'success') {
      toast.success(message, { id: toastId });
    } else if (type === 'error') {
      toast.error(message, { id: toastId });
    } else {
      toast.loading(message, { id: toastId });
    }
  }

  /**
   * Show API error notification with proper formatting
   */
  apiError(error: any): string {
    let message = 'An unexpected error occurred';

    if (error?.response?.data?.message) {
      message = error.response.data.message;
    } else if (error?.message) {
      message = error.message;
    }

    // Handle validation errors
    if (error?.response?.data?.errors) {
      const errors = error.response.data.errors;
      const firstError = Object.values(errors)[0] as string[];
      if (firstError && firstError.length > 0) {
        message = firstError[0];
      }
    }

    return this.error(message);
  }

  /**
   * Show network error notification
   */
  networkError(): string {
    return this.error('Network error. Please check your connection and try again.');
  }

  /**
   * Show offline notification
   */
  offline(): string {
    return this.warning('You are currently offline. Some features may not work.');
  }

  /**
   * Show back online notification
   */
  backOnline(): string {
    return this.success('You are back online!');
  }

  /**
   * Show form validation error
   */
  validationError(message: string = 'Please check the form for errors'): string {
    return this.error(message);
  }

  /**
   * Show permission denied error
   */
  permissionDenied(): string {
    return this.error('You do not have permission to perform this action.');
  }

  /**
   * Show not found error
   */
  notFound(resource: string = 'Resource'): string {
    return this.error(`${resource} not found.`);
  }

  /**
   * Show rate limit error
   */
  rateLimited(): string {
    return this.warning('Too many requests. Please wait a moment and try again.');
  }

  /**
   * Show maintenance mode notification
   */
  maintenance(): string {
    return this.info('The system is currently under maintenance. Please try again later.');
  }

  /**
   * Show feature coming soon notification
   */
  comingSoon(feature: string = 'This feature'): string {
    return this.info(`${feature} is coming soon!`);
  }

  /**
   * Show copy to clipboard success
   */
  copied(item: string = 'Text'): string {
    return this.success(`${item} copied to clipboard!`);
  }

  /**
   * Show save success
   */
  saved(item: string = 'Changes'): string {
    return this.success(`${item} saved successfully!`);
  }

  /**
   * Show delete success
   */
  deleted(item: string = 'Item'): string {
    return this.success(`${item} deleted successfully!`);
  }

  /**
   * Show update success
   */
  updated(item: string = 'Item'): string {
    return this.success(`${item} updated successfully!`);
  }

  /**
   * Show create success
   */
  created(item: string = 'Item'): string {
    return this.success(`${item} created successfully!`);
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;